// src/components/Badge.jsx
// Brutalist badge components - sharp edges, high contrast

const Badge = ({
    children,
    variant = 'default',
    size = 'sm',
    className = ''
}) => {
    // Refined: gentle radius, monospace, high-contrast status colors kept
    const baseClasses = 'inline-flex items-center font-mono font-bold uppercase rounded tracking-wide';

    const sizeClasses = {
        xs: 'px-1.5 py-0.5 text-[10px]',
        sm: 'px-2 py-0.5 text-xs',
        md: 'px-2.5 py-1 text-xs',
        lg: 'px-3 py-1 text-sm'
    };

    // Soft-tint light badges — modern, readable, coherent
    const variantClasses = {
        default: 'bg-slate-100 text-slate-600 border border-slate-200',
        primary: 'bg-primary/10 text-primary-dark',
        secondary: 'bg-pink-100 text-pink-700',
        success: 'bg-emerald-100 text-emerald-700',
        warning: 'bg-amber-100 text-amber-800',
        error: 'bg-red-100 text-red-700',
        info: 'bg-blue-100 text-blue-700',

        // Status-specific variants
        active: 'bg-emerald-100 text-emerald-700',
        inactive: 'bg-slate-100 text-slate-500',
        pending: 'bg-amber-100 text-amber-800',
        running: 'bg-blue-100 text-blue-700',
        completed: 'bg-emerald-100 text-emerald-700',
        failed: 'bg-red-100 text-red-700',
        cancelled: 'bg-slate-100 text-slate-500',

        // Subscription status
        trial: 'bg-purple-100 text-purple-700',
        subscribed: 'bg-emerald-100 text-emerald-700',
        expired: 'bg-red-100 text-red-700',

        // Tender status
        open: 'bg-emerald-100 text-emerald-700',
        closed: 'bg-slate-100 text-slate-500',
        awarded: 'bg-blue-100 text-blue-700',

        // Assessment status
        relevant: 'bg-emerald-100 text-emerald-700',
        'not-relevant': 'bg-red-100 text-red-700',
        'not-lucrative': 'bg-amber-100 text-amber-800',

        // Processing state variants
        draft: 'bg-slate-100 text-slate-500',
        discovered: 'bg-blue-100 text-blue-700',
        crawling: 'bg-amber-100 text-amber-800',
        crawled: 'bg-emerald-100 text-emerald-700',
        enriching: 'bg-amber-100 text-amber-800',
        enriched: 'bg-emerald-100 text-emerald-700',
        processing: 'bg-amber-100 text-amber-800',
        processed: 'bg-emerald-100 text-emerald-700',
        assessing: 'bg-amber-100 text-amber-800',
        assessed: 'bg-emerald-100 text-emerald-700',
        archived: 'bg-slate-100 text-slate-500'
    };

    const classes = `${baseClasses} ${sizeClasses[size]} ${variantClasses[variant] || variantClasses.default} ${className}`;

    return (
        <span className={classes}>
            {children}
        </span>
    );
};

// Helper function to determine badge variant based on status
export const getStatusVariant = (status) => {
    if (!status) return 'default';

    const statusLower = status.toLowerCase();

    const statusMap = {
        'active': 'active',
        'inactive': 'inactive',
        'pending': 'pending',
        'running': 'running',
        'completed': 'completed',
        'success': 'success',
        'failed': 'failed',
        'error': 'failed',
        'cancelled': 'cancelled',
        'trial': 'trial',
        'subscribed': 'subscribed',
        'expired': 'expired',
        'open': 'open',
        'closed': 'closed',
        'awarded': 'awarded',
        'relevant': 'relevant',
        'not_relevant': 'not-relevant',
        'not-relevant': 'not-relevant',
        'not_lucrative': 'not-lucrative',
        'not-lucrative': 'not-lucrative',
        'crawling': 'crawling',
        'assessing': 'assessing',
        'crawled': 'crawled',
        'assessed': 'assessed',
        'crawl_failed': 'failed',
        'assessment_failed': 'failed',
        'draft': 'draft',
        'discovered': 'discovered',
        'enriching': 'enriching',
        'enriched': 'enriched',
        'processing': 'processing',
        'processed': 'processed',
        'archived': 'archived'
    };

    return statusMap[statusLower] || 'default';
};

// Status Badge - automatically chooses variant based on status
export const StatusBadge = ({ status, size = 'sm', className = '' }) => {
    const variant = getStatusVariant(status);
    const displayText = status?.replace(/_/g, ' ').replace(/-/g, ' ') || 'UNKNOWN';

    return (
        <Badge variant={variant} size={size} className={className}>
            {displayText}
        </Badge>
    );
};

// Role Badge - for user roles
export const RoleBadge = ({ role, size = 'sm' }) => {
    const roleVariants = {
        'ADMIN': 'error',
        'MANAGER': 'warning',
        'USER': 'info',
        'SALES': 'success'
    };

    const variant = roleVariants[role] || 'default';

    return (
        <Badge variant={variant} size={size}>
            {role || 'UNKNOWN'}
        </Badge>
    );
};

// Priority Badge - for priorities/complexity
export const PriorityBadge = ({ priority, size = 'sm' }) => {
    const priorityVariants = {
        'HIGH': 'error',
        'MEDIUM': 'warning',
        'LOW': 'success',
        'CRITICAL': 'error'
    };

    const variant = priorityVariants[priority] || 'default';

    return (
        <Badge variant={variant} size={size}>
            {priority || 'UNKNOWN'}
        </Badge>
    );
};

// Tier Badge - for subscription tiers
export const TierBadge = ({ tier, size = 'sm' }) => {
    const tierVariants = {
        'FREE': 'default',
        'BASIC': 'info',
        'PRO': 'primary',
        'PROFESSIONAL': 'primary',
        'ENTERPRISE': 'secondary'
    };

    const variant = tierVariants[tier] || 'default';

    return (
        <Badge variant={variant} size={size}>
            {tier || 'UNKNOWN'}
        </Badge>
    );
};

export default Badge;
